// react-redux imports
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/store";

// common imports
import { TooltipAction, TOOLTIP_ACTIONS } from "common/constants/tooltip";
import FlexColumn from "common/components/styled-components/FlexColumn";
import FlexSpan from "common/components/styled-components/FlexSpan";
import PopUpParagraph from "common/components/styled-components/PopUpParagraph";
import SectionTitle from "common/components/styled-components/SectionTitle";
import SectionTitleIndication from "common/components/styled-components/SectionTitleIndication";
import Slider from "common/components/Slider";
import SliderLabel from "common/components/styled-components/InputGroupLabel";
import Tooltip from "common/components/Tooltip";
import TooltipPopUp from "common/components/TooltipPopUp";

import UserInputSection from "../styled-components/UserInputSection";
import {
  setDieselExpenseMonthly,
  setLPGExpenseMonthly,
  setNaturalGasExpenseMonthly,
} from "./energySlice";

interface Tooltips {
  naturalGas: boolean;
  diesel: boolean;
  LPG: boolean;
}

const initialTooltipsState: Tooltips = {
  naturalGas: false,
  diesel: false,
  LPG: false,
};

function StepTwo() {
  const [tooltips, setTooltips] = useState<Tooltips>(initialTooltipsState);

  const dispatch = useDispatch();
  const upDesktopScreen = useSelector(
    (state: RootState) => state.app.upDesktopScreen
  );
  const { naturalGasExpenseMonthly, dieselExpenseMonthly, LPGExpenseMonthly } =
    useSelector((state: RootState) => state.calculator.energy);

  const [naturalGasExpense, setNaturalGasExpense] = useState([
    naturalGasExpenseMonthly,
  ]);
  const [dieselExpense, setDieselExpense] = useState([dieselExpenseMonthly]);
  const [LPGExpense, setLPGExpense] = useState([LPGExpenseMonthly]);

  function handleNaturalGasFinalChange(values: number[]) {
    dispatch(setNaturalGasExpenseMonthly(values[0]));
  }

  function handleDieselFinalChange(values: number[]) {
    dispatch(setDieselExpenseMonthly(values[0]));
  }

  function handleLPGFinalChange(values: number[]) {
    dispatch(setLPGExpenseMonthly(values[0]));
  }

  function handleTooltip(tooltip: keyof Tooltips, action: TooltipAction) {
    switch (action) {
      case TOOLTIP_ACTIONS.OPEN:
        return () => setTooltips({ ...initialTooltipsState, [tooltip]: true });

      case TOOLTIP_ACTIONS.CLOSE:
        return () => setTooltips(initialTooltipsState);

      case TOOLTIP_ACTIONS.TOGGLE:
        return () =>
          setTooltips({
            ...initialTooltipsState,
            [tooltip]: !tooltips[tooltip],
          });

      default:
        return () => undefined;
    }
  }

  return (
    <>
      <SectionTitle>
        <span>¿Cuál es tu consumo medio de estas energías? </span>
        <SectionTitleIndication>(mensual, en €)</SectionTitleIndication>
        {tooltips.naturalGas && (
          <TooltipPopUp
            title="Gas natural"
            handleClose={handleTooltip("naturalGas", TOOLTIP_ACTIONS.CLOSE)}
          >
            <PopUpParagraph>
              El gas natural es un hidrocarburo mezcla de gases ligeros de
              origen natural. Es habitual en calderas, calentadores, y
              acumuladores. En menor medida existen cocinas con gas natural y
              gasodomésticos
            </PopUpParagraph>
          </TooltipPopUp>
        )}
        {tooltips.diesel && (
          <TooltipPopUp
            title="Gasóleo"
            handleClose={handleTooltip("diesel", TOOLTIP_ACTIONS.CLOSE)}
          >
            <PopUpParagraph>
              El gas natural es un hidrocarburo mezcla de gases ligeros de
              origen natural. Es habitual en calderas, calentadores, y
              acumuladores. En menor medida existen cocinas con gas natural y
              gasodomésticos
            </PopUpParagraph>
          </TooltipPopUp>
        )}
        {tooltips.LPG && (
          <TooltipPopUp
            title="Gases licuados del petróleo"
            handleClose={handleTooltip("LPG", TOOLTIP_ACTIONS.CLOSE)}
          >
            <PopUpParagraph>
              Es una mezcla de hidrocarburos derivados del petróleo los que a
              temperatura y presión normales se encuentran en estado gaseoso.
              Sirve como combustible para el coche y como fuente de energía en
              los hogares como para cocinar, calentar el agua y sirve como
              combustible para generar energía eléctrica
            </PopUpParagraph>
          </TooltipPopUp>
        )}
      </SectionTitle>
      <UserInputSection>
        <FlexColumn gap={upDesktopScreen ? 12 : 4}>
          <SliderLabel>
            <span>
              Gas{" "}
              <FlexSpan>
                natural&nbsp;
                <Tooltip
                  size={17}
                  onClick={handleTooltip("naturalGas", TOOLTIP_ACTIONS.TOGGLE)}
                />
              </FlexSpan>
            </span>
          </SliderLabel>
          <Slider
            min={0}
            max={150}
            maxIndicator="+150"
            thumbSize={upDesktopScreen ? 48 : 40}
            trackHeight={upDesktopScreen ? 8 : 12}
            values={naturalGasExpense}
            onChange={setNaturalGasExpense}
            onFinalChange={handleNaturalGasFinalChange}
          />
        </FlexColumn>
        <FlexColumn gap={upDesktopScreen ? 12 : 4}>
          <SliderLabel>
            <FlexSpan>
              Gasóleo&nbsp;
              <Tooltip
                size={17}
                onClick={handleTooltip("diesel", TOOLTIP_ACTIONS.TOGGLE)}
              />
            </FlexSpan>
          </SliderLabel>
          <Slider
            min={0}
            max={150}
            maxIndicator="+150"
            thumbSize={upDesktopScreen ? 48 : 40}
            trackHeight={upDesktopScreen ? 8 : 12}
            values={dieselExpense}
            onChange={setDieselExpense}
            onFinalChange={handleDieselFinalChange}
          />
        </FlexColumn>
        <FlexColumn gap={upDesktopScreen ? 12 : 4}>
          <SliderLabel>
            <span>
              Gases licuados del{" "}
              <FlexSpan>
                petróleo&nbsp;
                <Tooltip
                  size={17}
                  onClick={handleTooltip("LPG", TOOLTIP_ACTIONS.TOGGLE)}
                />
              </FlexSpan>
            </span>
          </SliderLabel>
          <Slider
            min={0}
            max={150}
            maxIndicator="+150"
            thumbSize={upDesktopScreen ? 48 : 40}
            trackHeight={upDesktopScreen ? 8 : 12}
            values={LPGExpense}
            onChange={setLPGExpense}
            onFinalChange={handleLPGFinalChange}
          />
        </FlexColumn>
      </UserInputSection>
    </>
  );
}

export default StepTwo;
